'use client';
import React from 'react';
import { useModel } from '@/context/ModelContext';
import { fmtCrShort, fmtNum } from '@/lib/financial-model';
import InputRow from './InputRow';

export default function FinancingSection() {
  const { assumptions, updateAssumption, model } = useModel();

  const equityPct =
    model?.totalProjectCost > 0 ? (assumptions?.equityAmount / model?.totalProjectCost) * 100 : 0;
  const debtPct =
    model?.totalProjectCost > 0 ? (assumptions?.debtAmount / model?.totalProjectCost) * 100 : 0;

  return (
    <div>
      <InputRow
        label="Equity — Partner Cash"
        unit="₹"
        note={`${equityPct?.toFixed(1)}% of total project cost — promoter contribution`}
        confidence="High"
        value={assumptions?.equityAmount}
        onChange={(v) => updateAssumption('equityAmount', v)}
        min={1000000}
        max={100000000}
        step={500000}
      />
      <InputRow
        label="Debt — Business Loan / CGTMSE"
        unit="₹"
        note={`${debtPct?.toFixed(1)}% of total project cost — CGTMSE-backed MSME term loan`}
        confidence="Medium"
        value={assumptions?.debtAmount}
        onChange={(v) => updateAssumption('debtAmount', v)}
        min={0}
        max={150000000}
        step={500000}
      />
      <InputRow
        label="Interest Rate on Debt"
        unit="% per annum"
        note="MSME / CGTMSE typical floating rate — fix if possible to reduce refinancing risk"
        confidence="Medium"
        value={assumptions?.interestRate}
        onChange={(v) => updateAssumption('interestRate', v)}
        min={6}
        max={20}
        step={0.25}
        isPercent
      />
      <InputRow
        label="Loan Tenure"
        unit="Years"
        note="Total tenure including moratorium period"
        confidence="Medium"
        value={assumptions?.loanTenure}
        onChange={(v) => updateAssumption('loanTenure', Math.round(v))}
        min={3}
        max={15}
        step={1}
      />
      <InputRow
        label="Moratorium Period"
        unit="Years"
        note="Interest-only period during construction and ramp-up — no principal repayment"
        confidence="Medium"
        value={assumptions?.moratorium}
        onChange={(v) => updateAssumption('moratorium', Math.round(v))}
        min={0}
        max={3}
        step={1}
      />
      <InputRow
        label="Depreciation Period (Machinery + Civil + Electrical)"
        unit="Years"
        note="Straight-line method on depreciable assets"
        confidence="High"
        value={assumptions?.depreciationPeriod}
        onChange={(v) => updateAssumption('depreciationPeriod', Math.round(v))}
        min={5}
        max={20}
        step={1}
      />
      <InputRow
        label="Effective Tax Rate"
        unit="% of PBT"
        note="New manufacturing company — 22% base + surcharge ≈ 25% effective"
        confidence="Medium"
        value={assumptions?.taxRate}
        onChange={(v) => updateAssumption('taxRate', v)}
        min={0}
        max={40}
        step={0.5}
        isPercent
      />
      <InputRow
        label="Annual Debt Service (PMT)"
        unit="₹ / year"
        note={`Calculated: PMT(${(assumptions?.interestRate * 100)?.toFixed(1)}%, ${assumptions?.loanTenure - assumptions?.moratorium} yrs, ₹${fmtNum(assumptions?.debtAmount)})`}
        isCalculated
        calculatedValue={`₹${fmtNum(Math.abs(model?.annualDebtService))}`}
        value={Math.abs(model?.annualDebtService)}
        onChange={() => {}}
      />
      {/* Debt Service Schedule */}
      <div className="mt-4">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
          Simplified Interest Schedule
        </p>
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-xs">
            <thead>
              <tr className="section-header-row">
                <th className="text-left py-2 px-3 rounded-tl-lg">Year</th>
                <th className="text-right py-2 px-3">Outstanding Debt</th>
                <th className="text-right py-2 px-3">Interest</th>
                <th className="text-right py-2 px-3">Principal Repaid</th>
                <th className="text-right py-2 px-3 rounded-tr-lg">Type</th>
              </tr>
            </thead>
            <tbody>
              {model?.years?.slice(0, 9)?.map((y) => {
                const isMoretarium = y?.year > 0 && y?.year <= assumptions?.moratorium;
                const isRepaying =
                  y?.year > assumptions?.moratorium && y?.year <= assumptions?.loanTenure;
                const isRepaid = y?.year > assumptions?.loanTenure;
                return (
                  <tr
                    key={`debt-row-${y?.year}`}
                    className={`border-b border-border/50 hover:bg-muted/30 transition-colors ${y?.year === 0 ? 'bg-info-bg' : ''}`}
                  >
                    <td className="py-2 px-3 text-foreground font-medium">
                      {y?.year === 0 ? 'Yr 0 (Drawdown)' : `Year ${y?.year}`}
                    </td>
                    <td className="py-2 px-3 text-right font-mono-nums text-foreground">
                      {fmtCrShort(y?.outstandingDebt)}
                    </td>
                    <td className="py-2 px-3 text-right font-mono-nums text-danger">
                      {y?.interest > 0 ? fmtCrShort(y?.interest) : '—'}
                    </td>
                    <td className="py-2 px-3 text-right font-mono-nums text-warning">
                      {y?.principalRepayment > 0 ? fmtCrShort(y?.principalRepayment) : '—'}
                    </td>
                    <td className="py-2 px-3 text-right">
                      {y?.year === 0 && <span className="badge-info text-xs">Drawdown</span>}
                      {isMoretarium && <span className="badge-warning text-xs">Moratorium</span>}
                      {isRepaying && <span className="badge-success text-xs">Repaying</span>}
                      {isRepaid && <span className="text-xs text-muted-foreground">Cleared</span>}
                      {y?.year === 0 ? null : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {/* Sources of funds */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="p-3 bg-success-bg border border-green-200 rounded-lg">
          <p className="text-xs text-muted-foreground">Partner Equity</p>
          <p className="font-mono text-base font-bold text-primary">
            {fmtCrShort(assumptions?.equityAmount)}
          </p>
          <p className="text-xs text-muted-foreground">{equityPct?.toFixed(1)}% of project cost</p>
        </div>
        <div className="p-3 bg-info-bg border border-blue-200 rounded-lg">
          <p className="text-xs text-muted-foreground">Debt (CGTMSE)</p>
          <p className="font-mono text-base font-bold text-accent">
            {fmtCrShort(assumptions?.debtAmount)}
          </p>
          <p className="text-xs text-muted-foreground">{debtPct?.toFixed(1)}% of project cost</p>
        </div>
        <div className="p-3 bg-muted border border-border rounded-lg">
          <p className="text-xs text-muted-foreground">Total Sources</p>
          <p className="font-mono text-base font-bold text-foreground">
            {fmtCrShort(assumptions?.equityAmount + assumptions?.debtAmount)}
          </p>
          <p
            className={`text-xs font-medium ${Math.abs(assumptions?.equityAmount + assumptions?.debtAmount - model?.totalProjectCost) < 1 ? 'text-primary' : 'text-danger'}`}
          >
            {Math.abs(
              assumptions?.equityAmount + assumptions?.debtAmount - model?.totalProjectCost
            ) < 1
              ? '✓ Balanced'
              : `Gap: ${fmtCrShort(Math.abs(assumptions?.equityAmount + assumptions?.debtAmount - model?.totalProjectCost))}`}
          </p>
        </div>
      </div>
    </div>
  );
}
